import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { submitContact } from "@/lib/api";

const INITIAL = { name: "", email: "", company: "", interest: "", message: "" };

const inputCls =
  "bg-transparent border-0 border-b border-[#1C2220]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C56A4E]";

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you — the Federation team will follow up directly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#EEEAE0] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-6">Contact</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C2220]">
              Let's <span className="italic text-[#C56A4E]">talk.</span>
            </h2>
            <p className="mt-8 text-base leading-relaxed text-[#1C2220]/70 max-w-md">
              Owners, brokers, housing authorities, and capital partners are encouraged
              to connect with our team. We acquire and preserve affordable housing across
              the Carolinas with a focus on long-term stewardship, resident stability, and
              continuity of existing affordability programs.
            </p>

            <div className="mt-12 space-y-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1C2220]/40">Email</div>
                <a href="mailto:info@federationco.com" className="mt-1 block font-heading text-2xl text-[#1C2220] hover:text-[#C56A4E] transition-colors" data-testid="contact-email-info">info@federationco.com</a>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1C2220]/40">Operating in</div>
                <div className="mt-1 font-heading text-xl text-[#1C2220]">North Carolina &amp; South Carolina</div>
              </div>
            </div>
          </motion.div>

          <motion.form onSubmit={onSubmit} data-testid="contact-form" initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 lg:col-start-7 bg-[#F7F5F0] border border-[#1C2220]/10 rounded-sm p-8 md:p-10">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-[#1C2220]/60">Full name *</Label>
                <Input id="name" data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} placeholder="Your name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#1C2220]/60">Email *</Label>
                <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="you@company.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-xs uppercase tracking-[0.2em] text-[#1C2220]/60">Company / organization</Label>
                <Input id="company" data-testid="contact-company" value={form.company} onChange={(e) => update("company", e.target.value)} className={inputCls} placeholder="Optional" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#1C2220]/60">Reaching out about</Label>
                <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                  <SelectTrigger data-testid="contact-interest" className="bg-transparent border-0 border-b border-[#1C2220]/20 rounded-none px-0 focus:ring-0">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Selling a property">Selling a property</SelectItem>
                    <SelectItem value="Investment or capital partnership">Investment or capital partnership</SelectItem>
                    <SelectItem value="Resident or property management inquiry">Resident / property management</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 mt-6">
              <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-[#1C2220]/60">Message *</Label>
              <Textarea id="message" data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4}
                className={`${inputCls} resize-none`} placeholder="Tell us about your community or opportunity..." />
            </div>

            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-sm bg-[#1C2220] text-white text-sm px-8 py-4 hover:bg-[#2A332F] hover:-translate-y-0.5 transition-[transform,background-color] duration-300 disabled:opacity-60 group">
              {loading ? "Sending..." : "Send Message"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
